import { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Grid, Typography, TextField, Button, Box, Chip, IconButton, CircularProgress, Link, Snackbar, Alert,
  FormControl, Select, MenuItem
} from "@mui/material";
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';

import backendAxios from "../../../utils/backendAxios";
import { loadUserClient } from "../../../actions/auth";
import { urlImg } from "../../common/helpers";

const STATUS = {
  0: { label: 'Sin enviar', bg: '#F1F5F9', color: '#737791' },
  1: { label: 'En revisión', bg: '#FFF3D6', color: '#92600E' },
  2: { label: 'Aprobado', bg: '#D6F3E8', color: '#1B7A5A' },
  3: { label: 'Rechazado', bg: '#FFE0DD', color: '#B23B33' },
};

// Filtro de formato (validación mínima; el superadmin hace la validación real después).
// RFC: 12 (moral) o 13 (física) caracteres — 3-4 letras + fecha (6 díg.) + homoclave (3).
// CURP: 18 caracteres — 4 letras + fecha (6 díg.) + sexo (H/M) + 5 letras + homoclave + díg.
const RFC_RE = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;
const CURP_RE = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;

const SECCIONES = [
  { tipo: 'titulos', label: 'Títulos profesionales', hint: 'Puedes subir varios.' },
  { tipo: 'certificados', label: 'Certificados', hint: 'Puedes subir varios.' },
  { tipo: 'otros', label: 'Otros documentos', hint: 'Opcional.' },
];

const vacia = { rfc: { valor: '', status: 0 }, curp: { valor: '', status: 0 }, titulos: [], certificados: [], otros: [], cedulas: [] };

const ChipStatus = ({ status }) => {
  const s = STATUS[status] || STATUS[0];
  return <Chip label={s.label} size="small" sx={{ height: 22, bgcolor: s.bg, color: s.color, fontWeight: 700 }} />;
};

const Documentos = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const userId = user?._id;

  const [verif, setVerif] = useState(vacia);
  const [rfc, setRfc] = useState('');
  const [curp, setCurp] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(null); // tipo en subida
  const [toast, setToast] = useState(null);

  // Cédulas profesionales: categorías que la exigen + formulario de alta.
  const [cats, setCats] = useState([]);
  const [cedForm, setCedForm] = useState({ categoria: '', numero: '' });
  const [subiendoCedula, setSubiendoCedula] = useState(false);

  const inputRef = useRef(null);
  const tipoRef = useRef(null);
  const cedInputRef = useRef(null);

  // Carga inicial desde el usuario en Redux.
  useEffect(() => {
    const v = user?.verificacion;
    if (v) {
      setVerif({ ...vacia, ...v });
      setRfc(v.rfc?.valor || '');
      setCurp(v.curp?.valor || '');
    }
  }, [user]);

  // Categorías del catálogo (para saber cuáles exigen cédula y etiquetar las subidas).
  useEffect(() => {
    backendAxios.get('/api/category')
      .then((res) => setCats(res.data || []))
      .catch(() => { /* sin catálogo: la sección de cédula no aplica */ });
  }, []);

  const cedulaCats = cats.filter((c) => c.requiereCedula);
  const catNombre = {};
  cats.forEach((c) => { catNombre[c._id] = c.name; });

  const guardarDatos = async () => {
    // Filtro previo: no se envía nada mal formado (el admin valida el resto después).
    if ((rfc.trim() && !RFC_RE.test(rfc.trim().toUpperCase())) ||
        (curp.trim() && !CURP_RE.test(curp.trim().toUpperCase()))) {
      setToast({ tipo: 'error', msg: 'Revisa el formato del RFC o la CURP antes de guardar.' });
      return;
    }
    setGuardando(true);
    try {
      const res = await backendAxios.patch(`/api/users/client/verificacion/datos/${userId}`, { rfc, curp });
      setVerif((p) => ({ ...p, ...res.data }));
      dispatch(loadUserClient());
      setToast({ tipo: 'success', msg: 'Datos guardados. Quedan en revisión.' });
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudieron guardar los datos.' });
    } finally {
      setGuardando(false);
    }
  };

  const elegirArchivo = (tipo) => {
    tipoRef.current = tipo;
    inputRef.current?.click();
  };

  const subirArchivo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const tipo = tipoRef.current;
    if (!file || !tipo) return;

    setSubiendo(tipo);
    try {
      const nombre = `${tipo}-${Date.now()}`;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('carpeta', `users/${userId}`);
      fd.append('nombre', nombre);
      fd.append('nuevo', true);
      const up = await backendAxios.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const archivo = `${nombre}.${up.data.format}`;

      const res = await backendAxios.post(`/api/users/client/verificacion/doc/${userId}`, { tipo, archivo, nombre: file.name });
      setVerif((p) => ({ ...p, ...res.data }));
      dispatch(loadUserClient());
      setToast({ tipo: 'success', msg: 'Documento subido. Queda en revisión.' });
    } catch (err) {
      setToast({ tipo: 'error', msg: 'No se pudo subir el documento.' });
    } finally {
      setSubiendo(null);
    }
  };

  const eliminarDoc = async (tipo, docId) => {
    try {
      const res = await backendAxios.delete(`/api/users/client/verificacion/doc/${userId}/${tipo}/${docId}`);
      setVerif((p) => ({ ...p, ...res.data }));
      dispatch(loadUserClient());
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudo eliminar el documento.' });
    }
  };

  // ---- Cédula profesional ----
  const elegirCedula = () => {
    if (!cedForm.categoria) { setToast({ tipo: 'error', msg: 'Elige la categoría de la cédula.' }); return; }
    cedInputRef.current?.click();
  };

  const subirCedula = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !cedForm.categoria) return;

    setSubiendoCedula(true);
    try {
      const nombre = `cedula-${Date.now()}`;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('carpeta', `users/${userId}`);
      fd.append('nombre', nombre);
      fd.append('nuevo', true);
      const up = await backendAxios.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const archivo = `${nombre}.${up.data.format}`;

      const res = await backendAxios.post(`/api/users/client/verificacion/cedula/${userId}`, {
        archivo, categoria: cedForm.categoria, numero: cedForm.numero.trim(), nombre: file.name,
      });
      setVerif((p) => ({ ...p, ...res.data }));
      dispatch(loadUserClient());
      setCedForm({ categoria: '', numero: '' });
      setToast({ tipo: 'success', msg: 'Cédula subida. Queda en revisión.' });
    } catch (err) {
      setToast({ tipo: 'error', msg: err.response?.data?.msg || 'No se pudo subir la cédula.' });
    } finally {
      setSubiendoCedula(false);
    }
  };

  const eliminarCedula = async (cedulaId) => {
    try {
      const res = await backendAxios.delete(`/api/users/client/verificacion/cedula/${userId}/${cedulaId}`);
      setVerif((p) => ({ ...p, ...res.data }));
      dispatch(loadUserClient());
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudo eliminar la cédula.' });
    }
  };

  const rfcVal = rfc.trim().toUpperCase();
  const curpVal = curp.trim().toUpperCase();
  const rfcCambiado = rfcVal !== (verif.rfc?.valor || '');
  const curpCambiado = curpVal !== (verif.curp?.valor || '');
  // Inválido solo si hay algo escrito y no cumple el formato (vacío = permitido).
  const rfcInvalido = rfcVal !== '' && !RFC_RE.test(rfcVal);
  const curpInvalido = curpVal !== '' && !CURP_RE.test(curpVal);

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" color="#0E2465" fontWeight="bold">Documentos y verificación</Typography>
        <Typography variant="body2" color="#737791" mt={0.5}>
          Captura tu RFC y CURP, y sube tus documentos. Un administrador los revisará.
        </Typography>
      </Box>

      {/* RFC y CURP (texto) */}
      <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#0E2465" fontWeight={500}>RFC</Typography>
              <ChipStatus status={verif.rfc?.status || 0} />
            </Box>
            <TextField fullWidth size="small" placeholder="RFC con homoclave" value={rfc}
              onChange={(e) => setRfc(e.target.value)} inputProps={{ style: { textTransform: 'uppercase' }, maxLength: 13 }}
              error={rfcInvalido}
              helperText={rfcInvalido ? 'RFC inválido: 12 o 13 caracteres con homoclave (ej. GODE561231GR8).' : ' '} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#0E2465" fontWeight={500}>CURP</Typography>
              <ChipStatus status={verif.curp?.status || 0} />
            </Box>
            <TextField fullWidth size="small" placeholder="Introduce tu CURP" value={curp}
              onChange={(e) => setCurp(e.target.value)} inputProps={{ style: { textTransform: 'uppercase' }, maxLength: 18 }}
              error={curpInvalido}
              helperText={curpInvalido ? 'CURP inválida: deben ser 18 caracteres (ej. GODE561231HDFXXX09).' : ' '} />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="contained" onClick={guardarDatos}
            disabled={guardando || rfcInvalido || curpInvalido || (!rfcCambiado && !curpCambiado)}
            sx={{ textTransform: 'none', fontWeight: 'bold', px: 4 }}>
            {guardando ? 'Guardando…' : 'Guardar datos'}
          </Button>
        </Box>
      </Box>

      {/* Cédula profesional (solo si alguna categoría la exige o ya subió alguna) */}
      {(cedulaCats.length > 0 || (verif.cedulas || []).length > 0) && (
        <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <WorkspacePremiumOutlinedIcon sx={{ color: 'secondary.main' }} />
            <Typography variant="body1" color="#0E2465" fontWeight={700}>Cédula profesional</Typography>
          </Box>
          <Typography variant="caption" color="#737791" sx={{ display: 'block', mb: 1.5 }}>
            Algunas categorías (ej. Abogado, Médico) solo publican tus servicios cuando validamos tu cédula.
            Sube una cédula por cada categoría que lo requiera.
          </Typography>

          {cedulaCats.length > 0 ? (
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 5 }}>
                <FormControl fullWidth size="small">
                  <Select value={cedForm.categoria} displayEmpty
                    onChange={(e) => setCedForm((f) => ({ ...f, categoria: e.target.value }))}>
                    <MenuItem value=""><em>Categoría de la cédula</em></MenuItem>
                    {cedulaCats.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" placeholder="Número de cédula (opcional)"
                  value={cedForm.numero} onChange={(e) => setCedForm((f) => ({ ...f, numero: e.target.value }))}
                  inputProps={{ maxLength: 15 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Button fullWidth variant="outlined" onClick={elegirCedula}
                  disabled={subiendoCedula || !cedForm.categoria}
                  startIcon={subiendoCedula ? <CircularProgress size={16} /> : <CloudUploadOutlinedIcon />}
                  sx={{ textTransform: 'none' }}>
                  Subir cédula
                </Button>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="#94A3B8">
              Ninguna de tus categorías requiere cédula por ahora.
            </Typography>
          )}

          {(verif.cedulas || []).length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
              {verif.cedulas.map((d) => (
                <Box key={d._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid #F1F5F9', borderRadius: 2 }}>
                  <WorkspacePremiumOutlinedIcon sx={{ color: '#64748B' }} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography noWrap variant="body2" sx={{ fontWeight: 600, color: '#0E2465' }}>
                      {catNombre[d.categoria] || d.nombre || 'Cédula'}{d.numero ? ` · Nº ${d.numero}` : ''}
                    </Typography>
                    <Link href={`${urlImg}/users/${userId}/${d.archivo}`} target="_blank" rel="noopener" sx={{ fontSize: 12 }}>Ver documento</Link>
                  </Box>
                  <ChipStatus status={d.status} />
                  <IconButton size="small" onClick={() => eliminarCedula(d._id)} disabled={d.status === 2}>
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Documentos */}
      <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2 }}>
        {SECCIONES.map((sec) => (
          <Box key={sec.tipo} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="body2" color="#0E2465" fontWeight={600}>{sec.label}</Typography>
                <Typography variant="caption" color="#737791">{sec.hint}</Typography>
              </Box>
              <Button variant="outlined" size="small" startIcon={subiendo === sec.tipo ? <CircularProgress size={16} /> : <CloudUploadOutlinedIcon />}
                onClick={() => elegirArchivo(sec.tipo)} disabled={!!subiendo} sx={{ textTransform: 'none' }}>
                Subir documento
              </Button>
            </Box>

            {(verif[sec.tipo] || []).length === 0 ? (
              <Typography variant="body2" color="#94A3B8" sx={{ py: 1 }}>Aún no has subido documentos.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {verif[sec.tipo].map((d) => (
                  <Box key={d._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid #F1F5F9', borderRadius: 2 }}>
                    <InsertDriveFileOutlinedIcon sx={{ color: '#64748B' }} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography noWrap variant="body2" sx={{ fontWeight: 500, color: '#0E2465' }}>{d.nombre || d.archivo}</Typography>
                      <Link href={`${urlImg}/users/${userId}/${d.archivo}`} target="_blank" rel="noopener" sx={{ fontSize: 12 }}>Ver</Link>
                    </Box>
                    <ChipStatus status={d.status} />
                    <IconButton size="small" onClick={() => eliminarDoc(sec.tipo, d._id)} disabled={d.status === 2}>
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <input ref={inputRef} type="file" hidden accept="image/*,application/pdf" onChange={subirArchivo} />
      <input ref={cedInputRef} type="file" hidden accept="image/*,application/pdf" onChange={subirCedula} />

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.tipo} onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default Documentos;
